/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { GanttRowData, GanttTaskData } from './gantt.types';
import { rowBackgroundStyles } from './themes/GanttStyles.css';
import type { ViewportLayout } from './utils/dataLayoutUtils';

type Props<K1, K2, D1 extends GanttRowData<K1, D1, D2>, D2 extends GanttTaskData<K2>> = {
  width: number;
  height: number;
  layoutObjs: ViewportLayout<K1, K2, D1, D2>['rowObjs'];
};

export function GanttRowBackgroundsLayer<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({ width, height, layoutObjs }: Props<K1, K2, D1, D2>) {
  const selectedRowObjs = layoutObjs.filter((rowObj) => rowObj.isSelected);
  const selectedRowsCmd = selectedRowObjs
    .map(({ y, height }) => {
      // Reduce left and right edge by 1px to ensure the rendering doesn't
      // interfere with the row axis separator
      return `M1,${y} H${width - 1} V${y + height} H${1} Z`;
    })
    .join(' ');

  return selectedRowObjs.length > 0 ? (
    <svg width={width} height={height}>
      <path class={rowBackgroundStyles.selected} d={selectedRowsCmd} />
    </svg>
  ) : null;
}
